import { useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from '@/components/ui';
import { cn } from '@/utils/cn';
import type { FarSideNoteEntry } from '@/services/templateApi';
import { FarSideNote } from './FarSideNote';

// ---- Types ----

interface GoalResult {
  goal_type: string;
  goal_label: string;
  score: number;
  passed: boolean;
  issue: string | null;
}

interface PhaseResult {
  phase_name: string;
  form_score: number;
  goals: GoalResult[];
  issues: string[];
}

interface ValidationReportResult {
  template_id: string;
  template_name: string;
  can_finalize: boolean;
  structural_errors: string[];
  validation_report: {
    overall_score: number;
    is_valid: boolean;
    phases: PhaseResult[];
    warnings: string[];
  } | null;
  quality_report: {
    overall_quality: string;
    score: number;
    checks: Array<{
      name: string;
      label: string;
      status: string;
      message: string;
      tip: string | null;
    }>;
  } | null;
  warnings: string[];
}

export interface ValidationReportProps {
  result: ValidationReportResult;
  onAccept: () => void;
  onGoBack: () => void;
  isAccepting: boolean;
  /**
   * `metadata.far_side_note` — checkpoints that sit on the side of the body
   * away from the camera in this demo (viewpoint observability Phase 1).
   * Shown here as well as on the template page because finalize is the last
   * moment the coach can cheaply decide to re-film instead. Not a blocker.
   */
  farSideNote?: FarSideNoteEntry[] | null;
}

// ---- Helpers ----

function makeIssueCoachFriendly(issue: string): string {
  if (!issue) return issue;

  if (issue.includes("reference_type='vertical'") || issue.includes('reference_type="vertical"')) {
    return 'This goal measures against absolute vertical instead of your template\'s angle — consider switching to a relative reference.';
  }
  if (issue.includes('0 valid frames')) {
    return 'The landmarks used by this goal could not be detected in your video. Check that the relevant body part is visible throughout the phase.';
  }
  if (issue.includes('GATE')) {
    return 'This goal is a pass/fail gate — failing it will zero the entire score for this phase.';
  }
  if (issue.includes('measure_stability=true') || issue.includes('measure_stability')) {
    return 'This goal is set to penalise natural movement variation. Make sure that is intentional.';
  }
  if (issue.includes('tolerance')) {
    return 'The scoring tolerance may be too tight and could unfairly penalise athletes.';
  }
  return issue;
}

function statusColor(status: string): string {
  switch (status) {
    case 'pass':
    case 'ok':
      return 'bg-success';
    case 'warn':
    case 'warning':
      return 'bg-warning';
    case 'fail':
    case 'error':
      return 'bg-danger';
    default:
      return 'bg-text-muted';
  }
}

function statusTextColor(status: string): string {
  switch (status) {
    case 'pass':
    case 'ok':
      return 'text-success';
    case 'warn':
    case 'warning':
      return 'text-warning';
    case 'fail':
    case 'error':
      return 'text-danger';
    default:
      return 'text-text-muted';
  }
}

// ---- Sub-components ----

function PhaseSection({ phase }: { phase: PhaseResult }) {
  const [open, setOpen] = useState(true);
  const failedGoals = phase.goals.filter((g) => !g.passed && g.issue);
  const hasIssues = failedGoals.length > 0 || phase.issues.length > 0;

  if (!hasIssues) return null;

  const scorePercent = Math.round(phase.form_score * 100);

  return (
    <div className="border border-border-subtle rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 bg-dark-elevated-1 hover:bg-dark-elevated-2 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-3">
          {open ? (
            <ChevronDownIcon className="h-4 w-4 text-text-muted flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-text-muted flex-shrink-0" />
          )}
          <span className="text-sm font-semibold text-text-primary">{phase.phase_name}</span>
        </div>
        <span
          className={cn(
            'text-xs font-mono px-2 py-0.5 rounded-full',
            scorePercent >= 70
              ? 'bg-success/15 text-success'
              : scorePercent >= 40
              ? 'bg-warning/15 text-warning'
              : 'bg-danger/15 text-danger'
          )}
        >
          Form {scorePercent}%
        </span>
      </button>

      {open && (
        <div className="px-4 py-3 space-y-3 bg-dark-card">
          {failedGoals.map((goal, idx) => (
            <div key={idx} className="flex gap-3 text-sm">
              <XCircleIcon className="h-4 w-4 text-danger flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-text-primary">{goal.goal_label}</p>
                {goal.issue && (
                  <p className="text-text-secondary mt-0.5">
                    {makeIssueCoachFriendly(goal.issue)}
                  </p>
                )}
              </div>
            </div>
          ))}

          {phase.issues.map((issue, idx) => (
            <div key={`issue-${idx}`} className="flex gap-3 text-sm">
              <ExclamationTriangleIcon className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
              <p className="text-text-secondary">{makeIssueCoachFriendly(issue)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Main component ----

export function ValidationReport({
  result,
  onAccept,
  onGoBack,
  isAccepting,
  farSideNote,
}: ValidationReportProps) {
  const { can_finalize, structural_errors, validation_report, quality_report, warnings } = result;

  // Count items needing attention
  const attentionCount =
    structural_errors.length +
    (quality_report?.checks.filter((c) => c.status !== 'pass' && c.status !== 'ok').length ?? 0) +
    (validation_report?.phases.reduce(
      (acc, p) => acc + p.goals.filter((g) => !g.passed && g.issue).length + p.issues.length,
      0
    ) ?? 0);

  // Header icon + copy
  let headerIcon: React.ReactNode;
  let headerTitle: string;
  let headerSubtitle: string;

  if (!can_finalize) {
    headerIcon = (
      <div className="rounded-full bg-danger/15 p-3">
        <XCircleIcon className="h-8 w-8 text-danger" />
      </div>
    );
    headerTitle = 'Template needs fixes before finalizing';
    headerSubtitle = 'Resolve the errors below, then try again.';
  } else if (warnings.length > 0 || attentionCount > 0) {
    headerIcon = (
      <div className="rounded-full bg-warning/15 p-3">
        <ExclamationTriangleIcon className="h-8 w-8 text-warning" />
      </div>
    );
    headerTitle = 'Template can be finalized, but review these items';
    headerSubtitle = 'These are not blockers — you can proceed, but addressing them may improve scoring accuracy.';
  } else {
    headerIcon = (
      <div className="rounded-full bg-success/15 p-3">
        <CheckCircleIcon className="h-8 w-8 text-success" />
      </div>
    );
    headerTitle = 'Template is ready!';
    headerSubtitle = 'Everything looks good. You can finalize this template.';
  }

  const phasesWithIssues = validation_report?.phases.filter(
    (p) => p.goals.some((g) => !g.passed && g.issue) || p.issues.length > 0
  ) ?? [];

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-6 py-5 flex items-start gap-4 border-b border-border-subtle">
        {headerIcon}
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-text-primary">{headerTitle}</h2>
          <p className="text-sm text-text-secondary mt-1">{headerSubtitle}</p>
          {attentionCount > 0 && (
            <span className="inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full bg-warning/15 text-warning">
              {attentionCount} item{attentionCount !== 1 ? 's' : ''} need attention
            </span>
          )}
        </div>
      </div>

      <div className="px-6 py-5 space-y-6">
        {/* Structural errors */}
        {structural_errors.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-danger mb-3 uppercase tracking-wide">
              Errors — must fix before finalizing
            </h3>
            <div className="bg-danger/10 border border-danger/30 rounded-lg p-4 space-y-2">
              {structural_errors.map((err, idx) => (
                <div key={idx} className="flex gap-2 text-sm">
                  <XCircleIcon className="h-4 w-4 text-danger flex-shrink-0 mt-0.5" />
                  <span className="text-text-primary">{err}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Checkpoints on the side away from the camera — informational, and
            the last cheap moment to re-film instead of accepting a less
            accurate check. */}
        {farSideNote && farSideNote.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-warning mb-3 uppercase tracking-wide">
              Checkpoints facing away from the camera
            </h3>
            <FarSideNote entries={farSideNote} />
          </section>
        )}

        {/* Quality checks */}
        {quality_report && quality_report.checks.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-text-secondary mb-3 uppercase tracking-wide">
              Quality checks
            </h3>
            <div className="space-y-2">
              {quality_report.checks.map((check, idx) => (
                <div key={idx} className="flex gap-3 py-2">
                  <span
                    className={cn(
                      'mt-1.5 h-2 w-2 rounded-full flex-shrink-0',
                      statusColor(check.status)
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-medium text-text-primary">{check.label}</span>
                      <span className={cn('text-xs', statusTextColor(check.status))}>
                        {check.message}
                      </span>
                    </div>
                    {check.tip && (
                      <p className="text-xs text-text-muted mt-0.5 italic">{check.tip}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Goal health per phase */}
        {phasesWithIssues.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-text-secondary mb-3 uppercase tracking-wide">
              Goal health
            </h3>
            <div className="space-y-2">
              {phasesWithIssues.map((phase, idx) => (
                <PhaseSection key={idx} phase={phase} />
              ))}
            </div>
          </section>
        )}

        {/* Additional warnings */}
        {warnings.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-warning mb-3 uppercase tracking-wide">
              Additional warnings
            </h3>
            <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 space-y-2">
              {warnings.map((w, idx) => (
                <div key={idx} className="flex gap-2 text-sm">
                  <ExclamationTriangleIcon className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
                  <span className="text-text-secondary">{w}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-border-subtle flex items-center justify-end gap-3 bg-dark-elevated-1 rounded-b-xl">
        {can_finalize ? (
          <>
            <button
              type="button"
              onClick={onGoBack}
              disabled={isAccepting}
              className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary border border-border-subtle rounded-lg hover:bg-dark-elevated-2 transition-colors disabled:opacity-50"
            >
              Go Back and Edit
            </button>
            <button
              type="button"
              onClick={onAccept}
              disabled={isAccepting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-success hover:bg-success/90 text-white rounded-lg transition-colors disabled:opacity-60"
            >
              {isAccepting ? (
                <>
                  <Spinner size="sm" />
                  Finalizing...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4" />
                  Accept and Finalize
                </>
              )}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onGoBack}
            className="px-4 py-2 text-sm font-semibold text-text-inverse bg-accent hover:bg-accent-hover rounded-lg transition-colors"
          >
            Go Back and Fix
          </button>
        )}
      </div>
    </div>
  );
}

export default ValidationReport;
